// Operaciones · Torniquete · Puertas
//
// ZKBio nombra las puertas por IP y número ("10.0.0.200-1"), que no le dice nada a nadie,
// y su API no permite renombrarlas. Así que el nombre legible se guarda de nuestro lado y
// se usa en todas las pantallas y en los reportes.
//
// La dirección no es decoración: el equipo NO distingue entrada de salida por sí mismo
// —reporta las dos puertas como entrada y las salidas por botón no identifican a nadie—,
// así que marcar una puerta como "salida" es lo único que permite calcular a qué hora se
// fue cada persona en la pestaña Asistencia.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Button, Alert, Input, Select, Modal, Form,
} from 'antd';
import { ReloadOutlined, EditOutlined, SwapOutlined } from '@ant-design/icons';
import { AccesoAdmin } from '../../../../service/Acceso';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Text, Paragraph } = Typography;

const DIRECCION = {
  entrada: { color: 'green', label: 'Entrada' },
  salida: { color: 'orange', label: 'Salida' },
  ambas: { color: 'default', label: 'Sin definir' },
};

export default function TorniquetePuertas() {
  const notify = useNotify();
  const [form] = Form.useForm();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await AccesoAdmin.puertas());
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, [notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirEdicion = (puerta) => {
    setEditando(puerta);
    form.setFieldsValue({
      etiqueta: puerta.etiqueta || '',
      direccion: puerta.direccion || 'ambas',
      notas: puerta.notas || '',
    });
  };

  const guardar = async () => {
    try {
      const valores = await form.validateFields();
      setGuardando(true);
      await AccesoAdmin.guardarPuerta({
        doorName: editando.doorName,
        devSn: editando.equipo,
        ...valores,
      });
      notify?.success?.(`"${valores.etiqueta}" guardada`);
      setEditando(null);
      cargar();
    } catch (e) {
      if (e?.errorFields) return;  // validación del form, ya se muestra en el campo
      notify?.error?.(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  };

  const haySalida = (datos?.puertas || []).some(p => p.direccion === 'salida');
  const sinEtiquetar = (datos?.puertas || []).filter(p => !p.etiqueta).length;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        size="small"
        title={<span><SwapOutlined /> Puertas del recinto</span>}
        extra={
          <Button size="small" icon={<ReloadOutlined />} loading={cargando} onClick={cargar}>
            Actualizar
          </Button>
        }
      >
        {datos?.errores?.length > 0 && (
          <Alert
            type="error" showIcon style={{ marginBottom: 12 }}
            message="El equipo no respondió el listado de puertas"
            description={datos.errores.join(' · ')}
          />
        )}

        {!haySalida && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message="Ninguna puerta está marcada como salida"
            description="Mientras no haya una, la asistencia solo puede mostrar la primera y la última pasada de cada persona, no su hora de salida."
          />
        )}

        <Table
          size="small"
          rowKey="doorName"
          dataSource={datos?.puertas || []}
          loading={cargando}
          pagination={false}
          locale={{ emptyText: 'El equipo no reportó puertas' }}
          columns={[
            {
              title: 'Nombre en el equipo',
              dataIndex: 'doorName',
              render: (v, r) => (
                <Space direction="vertical" size={0}>
                  <Text code>{v}</Text>
                  {r.soloEnLaBase && <Tag color="warning">el equipo ya no la reporta</Tag>}
                </Space>
              ),
            },
            {
              title: 'Etiqueta',
              render: (_, r) => (r.etiqueta
                ? <Text strong>{r.etiqueta}</Text>
                : <Text type="secondary">sin etiquetar</Text>
              ),
            },
            {
              title: 'Dirección',
              dataIndex: 'direccion',
              width: 130,
              render: (v) => <Tag color={DIRECCION[v]?.color}>{DIRECCION[v]?.label || v}</Tag>,
            },
            {
              title: 'Notas',
              dataIndex: 'notas',
              ellipsis: true,
              render: (v) => v || <Text type="secondary">—</Text>,
            },
            {
              title: 'Última edición',
              width: 190,
              render: (_, r) => (r.etiquetadaEn
                ? <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 12 }}>{r.etiquetadaEn}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.etiquetadaPor}</Text>
                  </Space>
                : <Text type="secondary">—</Text>
              ),
            },
            {
              title: '',
              width: 90,
              render: (_, r) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(r)}>
                  Editar
                </Button>
              ),
            },
          ]}
        />

        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          {sinEtiquetar > 0
            ? `${sinEtiquetar} puerta(s) sin etiquetar: mientras no tengan nombre se muestran por su IP en el resto del panel.`
            : 'Todas las puertas tienen nombre.'}
        </Paragraph>
      </Card>

      <Modal
        open={Boolean(editando)}
        title={`Etiquetar ${editando?.doorName || ''}`}
        onCancel={() => setEditando(null)}
        onOk={guardar}
        confirmLoading={guardando}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="etiqueta"
            label="Nombre"
            rules={[{ required: true, message: 'Ponle un nombre reconocible' }, { max: 60 }]}
            extra="Cómo la llaman en el recinto: Puerta principal, Torniquete nuevo, Acceso piscina…"
          >
            <Input placeholder="Puerta principal" />
          </Form.Item>

          <Form.Item
            name="direccion"
            label="Dirección"
            extra="Si marcas una puerta como salida, la asistencia usa sus lecturas para calcular la hora de salida y el tiempo en el recinto."
          >
            <Select
              options={[
                { value: 'entrada', label: 'Entrada — por acá se entra' },
                { value: 'salida', label: 'Salida — por acá se sale' },
                { value: 'ambas', label: 'Sin definir — se usa solo para contar pasadas' },
              ]}
            />
          </Form.Item>

          <Form.Item name="notas" label="Notas" rules={[{ max: 200 }]}>
            <Input.TextArea rows={2} placeholder="Ej: es el torniquete nuevo, el del lado de la reja" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
